import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, Eye, EyeOff, Flag } from 'lucide-react';
import { useAuthSafe } from '../../../contexts/AuthContext';
import { useDebugModeSafe } from '../../../contexts/DebugModeContext';
import { useActiveSessionIdSafe } from '../../../contexts/SessionContext';
import { apiService } from '../../../services/ApiService';

/**
 * Why this part — the four indications behind a result (Image analysis review,
 * 2026-10-03): Identity, Shape, Size, Position, each green (supports), amber
 * (an estimate / unsure), red (conflicts) or grey (not checked).
 *
 * Collapsed: four chips and one strength bar per indication. Expanded: one
 * plain-language line per indication (already in the customer's language,
 * written by the backend), a 3×3 position grid with the part in the centre,
 * "Show on photo" (anchors outlined in blue over the user's photo) and
 * "Not this part" (which indication is wrong → eval set).
 *
 * Evidence computed in shadow mode carries `shadow: true` and is shown only to
 * admins in debug mode — shadow never changes what a customer sees.
 */

export type IndicationKey = 'identity' | 'shape' | 'size' | 'position';
export type IndicationStatus = 'supports' | 'estimate' | 'conflict' | 'unknown';

export interface Box { x_min: number; y_min: number; x_max: number; y_max: number }

export interface MatchEvidence {
  indications: Record<IndicationKey, { status: IndicationStatus; why: string }>;
  match_strength?: number;
  shadow?: boolean;
  photo?: {
    source_image_uuid?: string | null;
    bbox?: Box | null;
    anchors?: Array<{ name: string; relation?: string; direction?: string; bbox?: Box | null }>;
  } | null;
}

export const INDICATIONS: IndicationKey[] = ['identity', 'shape', 'size', 'position'];

const TONE: Record<IndicationStatus, { dot: string; chip: string; bar: string; mark: string }> = {
  supports: { dot: '#16a34a', chip: 'bg-emerald-50 text-emerald-800 border-emerald-200', bar: '#16a34a', mark: '✓' },
  estimate: { dot: '#d97706', chip: 'bg-amber-50 text-amber-800 border-amber-200', bar: '#d97706', mark: '~' },
  conflict: { dot: '#dc2626', chip: 'bg-rose-50 text-rose-800 border-rose-200', bar: '#dc2626', mark: '✗' },
  unknown: { dot: '#a1a1aa', chip: 'bg-zinc-50 text-zinc-600 border-zinc-200', bar: '#d4d4d8', mark: '–' },
};
const FILL: Record<IndicationStatus, number> = { supports: 1, estimate: 0.6, unknown: 0.15, conflict: 0.05 };

export const useIndicationLabels = () => {
  const { t } = useTranslation();
  return {
    identity: t('chat.matchEvidence.identity', { defaultValue: 'Identity' }),
    shape: t('chat.matchEvidence.shape', { defaultValue: 'Shape' }),
    size: t('chat.matchEvidence.size', { defaultValue: 'Size' }),
    position: t('chat.matchEvidence.position', { defaultValue: 'Position' }),
  } as Record<IndicationKey, string>;
};

/** True when this viewer may see this evidence (shadow = admin debug only). */
export const useEvidenceVisible = (evidence?: MatchEvidence | null): boolean => {
  const currentUser = useAuthSafe()?.currentUser;
  const debugMode = useDebugModeSafe()?.debugMode;
  if (!evidence || !evidence.indications) return false;
  if (!evidence.shadow) return true;
  return currentUser?.role === 'admin' && !!debugMode;
};

const centre = (b?: Box | null): [number, number] | null =>
  b ? [(b.x_min + b.x_max) / 2, (b.y_min + b.y_max) / 2] : null;

/** Grid cell (row, col 0..2) of an anchor relative to the part in the centre. */
export function gridCell(
  anchor: { direction?: string; relation?: string; bbox?: Box | null },
  part?: Box | null,
): [number, number] | null {
  const a = centre(anchor.bbox);
  const p = centre(part);
  if (a && p) {
    const dx = a[0] - p[0];
    const dy = a[1] - p[1];
    const col = Math.abs(dx) < 120 ? 1 : dx < 0 ? 0 : 2;
    const row = Math.abs(dy) < 120 ? 1 : dy < 0 ? 0 : 2;
    return row === 1 && col === 1 ? null : [row, col];
  }
  // "position" says where the TARGET sits relative to the anchor — the anchor
  // is on the opposite side of the part.
  switch (anchor.direction) {
    case 'above': return [2, 1];
    case 'below': return [0, 1];
    case 'front': return [1, 0];
    case 'behind': return [1, 2];
    case 'inboard': return [1, 2];
    case 'outboard': return [1, 0];
    default: return null;
  }
}

const PositionGrid: React.FC<{ evidence: MatchEvidence; partName: string }> = ({ evidence, partName }) => {
  const cells: string[][] = [[], [], [], [], [], [], [], [], []];
  for (const a of evidence.photo?.anchors || []) {
    const cell = gridCell(a, evidence.photo?.bbox);
    if (cell) cells[cell[0] * 3 + cell[1]].push(a.name);
  }
  if (cells.every(c => c.length === 0)) return null;
  return (
    <div data-testid="match-position-grid" className="grid grid-cols-3 gap-0.5 w-full max-w-[260px] text-[10px] leading-tight">
      {cells.map((names, i) => (
        <div
          key={i}
          className={`min-h-[34px] rounded px-1 py-0.5 flex items-center justify-center text-center ${
            i === 4 ? 'bg-primary-50 text-primary-800 font-semibold border border-primary-200' : names.length ? 'bg-sky-50 text-sky-800 border border-sky-100' : 'bg-zinc-50'}`}
        >
          {i === 4 ? partName : names.join(', ')}
        </div>
      ))}
    </div>
  );
};

const PhotoOverlay: React.FC<{ url: string; evidence: MatchEvidence }> = ({ url, evidence }) => {
  const box = (b: Box, color: string, dashed = false, key?: React.Key) => (
    <div
      key={key}
      style={{
        position: 'absolute', left: `${b.x_min / 10}%`, top: `${b.y_min / 10}%`,
        width: `${(b.x_max - b.x_min) / 10}%`, height: `${(b.y_max - b.y_min) / 10}%`,
        border: `2px ${dashed ? 'dashed' : 'solid'} ${color}`, borderRadius: 4,
      }}
    />
  );
  return (
    <div data-testid="match-photo-overlay" className="relative w-full max-w-[360px] rounded-lg overflow-hidden border border-zinc-200">
      <img src={url} alt="" className="block w-full h-auto" />
      {evidence.photo?.bbox && box(evidence.photo.bbox, '#6866ff', true)}
      {(evidence.photo?.anchors || []).map((a, i) => (a.bbox ? (
        <React.Fragment key={i}>
          {box(a.bbox, '#2563eb')}
          <span
            style={{ position: 'absolute', left: `${a.bbox.x_min / 10}%`, top: `${a.bbox.y_min / 10}%` }}
            className="-translate-y-full bg-blue-600 text-white text-[9px] px-1 rounded-sm whitespace-nowrap"
          >
            {a.name}
          </span>
        </React.Fragment>
      ) : null))}
    </div>
  );
};

export const IndicationChips: React.FC<{ evidence: MatchEvidence }> = ({ evidence }) => {
  const labels = useIndicationLabels();
  return (
    <div className="flex flex-wrap items-center gap-1">
      {INDICATIONS.map(k => {
        const st = evidence.indications[k]?.status || 'unknown';
        return (
          <span
            key={k}
            data-testid={`chip-${k}`}
            data-status={st}
            title={evidence.indications[k]?.why || ''}
            className={`inline-flex items-center gap-1 text-[10.5px] font-semibold px-1.5 py-0.5 rounded-full border ${TONE[st].chip}`}
          >
            <span style={{ width: 6, height: 6, borderRadius: 9999, background: TONE[st].dot }} />
            {labels[k]}
          </span>
        );
      })}
    </div>
  );
};

/** One bar per indication (the Oct 2 match-strength bars). */
export const StrengthBars: React.FC<{ evidence: MatchEvidence }> = ({ evidence }) => {
  const { t } = useTranslation();
  const labels = useIndicationLabels();
  return (
    <div
      className="flex items-end gap-0.5"
      title={t('chat.matchEvidence.strength', { defaultValue: 'Match strength' }) as string}
      aria-label={`${t('chat.matchEvidence.strength', { defaultValue: 'Match strength' })}: ${Math.round((evidence.match_strength ?? 0) * 100)}%`}
    >
      {INDICATIONS.map(k => {
        const st = evidence.indications[k]?.status || 'unknown';
        return (
          <span key={k} title={labels[k]} className="w-1.5 h-3.5 rounded-sm bg-zinc-100 relative overflow-hidden">
            <span style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: `${FILL[st] * 100}%`, background: TONE[st].bar }} />
          </span>
        );
      })}
    </div>
  );
};

const NotThisPart: React.FC<{ partId: string; partName: string; messageId?: string }> = ({ partId, partName, messageId }) => {
  const { t } = useTranslation();
  const labels = useIndicationLabels();
  const sessionId = useActiveSessionIdSafe();
  const [open, setOpen] = React.useState(false);
  const [sent, setSent] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  if (!sessionId) return null;
  const send = async (indication: IndicationKey | 'other') => {
    setBusy(true);
    try {
      await apiService.post('/api/feedback/part-match', {
        session_id: sessionId, message_id: messageId, part_id: partId, part_name: partName, indication,
      });
      setSent(indication);
      setOpen(false);
    } catch {
      setSent(null);
    } finally {
      setBusy(false);
    }
  };
  if (sent) {
    return <span data-testid="not-this-part-sent" className="text-[11px] text-zinc-500">{t('chat.matchEvidence.feedbackThanks', { defaultValue: 'Thanks — noted.' })}</span>;
  }
  return (
    <span className="relative inline-block">
      <button type="button" onClick={() => setOpen(o => !o)} disabled={busy}
        className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700 hover:text-rose-900">
        <Flag size={11} /> {t('chat.matchEvidence.notThisPart', { defaultValue: 'Not this part' })}
      </button>
      {open && (
        <span className="absolute z-20 mt-1 left-0 flex flex-col bg-white border border-zinc-200 rounded-lg shadow-md p-1 min-w-[180px]">
          <span className="px-2 py-1 text-[10.5px] text-zinc-500">{t('chat.matchEvidence.whichWrong', { defaultValue: 'What is wrong?' })}</span>
          {[...INDICATIONS, 'other' as const].map(k => (
            <button key={k} type="button" onClick={() => send(k)} data-testid={`not-this-part-${k}`}
              className="text-left text-[12px] px-2 py-1 rounded hover:bg-zinc-50">
              {k === 'other' ? t('chat.matchEvidence.other', { defaultValue: 'Something else' }) : labels[k]}
            </button>
          ))}
        </span>
      )}
    </span>
  );
};

export const MatchEvidencePanel: React.FC<{
  evidence?: MatchEvidence | null;
  partId?: string | null;
  partName: string;
  photoUrl?: string | null;
  messageId?: string;
}> = ({ evidence, partId, partName, photoUrl, messageId }) => {
  const { t } = useTranslation();
  const labels = useIndicationLabels();
  const visible = useEvidenceVisible(evidence);
  const [expanded, setExpanded] = React.useState(false);
  const [showPhoto, setShowPhoto] = React.useState(false);
  if (!visible || !evidence) return null;
  const canOverlay = !!photoUrl && (evidence.photo?.anchors || []).some(a => a.bbox);
  return (
    <div data-testid="match-evidence" className="mt-1.5 rounded-lg border border-zinc-200 bg-white px-2 py-1.5">
      <div className="flex items-center gap-2">
        <IndicationChips evidence={evidence} />
        <StrengthBars evidence={evidence} />
        {evidence.shadow && <span className="text-[9.5px] uppercase tracking-wide text-zinc-400">shadow</span>}
        <button type="button" onClick={() => setExpanded(e => !e)} data-testid="match-evidence-toggle"
          className="ml-auto inline-flex items-center gap-0.5 text-[11px] font-semibold text-primary-700">
          {t('chat.matchEvidence.why', { defaultValue: 'Why this part' })}
          {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </button>
      </div>
      {expanded && (
        <div className="mt-2 space-y-2">
          <ul className="space-y-1">
            {INDICATIONS.map(k => {
              const ind = evidence.indications[k];
              if (!ind) return null;
              const st = ind.status || 'unknown';
              return (
                <li key={k} className="flex gap-2 text-[12px] leading-snug">
                  <span className="shrink-0 w-16 font-semibold" style={{ color: TONE[st].dot }}>{TONE[st].mark} {labels[k]}</span>
                  <span className="text-zinc-700">
                    {ind.why || t('chat.matchEvidence.notChecked', { defaultValue: 'Not checked from the photo.' })}
                  </span>
                </li>
              );
            })}
          </ul>
          <PositionGrid evidence={evidence} partName={partName} />
          <div className="flex items-center gap-3">
            {canOverlay && (
              <button type="button" onClick={() => setShowPhoto(s => !s)} data-testid="show-on-photo"
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700">
                {showPhoto ? <EyeOff size={12} /> : <Eye size={12} />}
                {showPhoto
                  ? t('chat.matchEvidence.hidePhoto', { defaultValue: 'Hide photo' })
                  : t('chat.matchEvidence.showOnPhoto', { defaultValue: 'Show on photo' })}
              </button>
            )}
            {partId && <NotThisPart partId={partId} partName={partName} messageId={messageId} />}
          </div>
          {showPhoto && photoUrl && <PhotoOverlay url={photoUrl} evidence={evidence} />}
        </div>
      )}
    </div>
  );
};

/**
 * Tied candidates side by side: one row per indication, a tick / cross per
 * candidate, and the deciding difference when one was found.
 */
export const MatchEvidenceCompare: React.FC<{
  options: Array<{ id: string; label: string; match_evidence?: MatchEvidence | null }>;
  decidingDifference?: string | null;
}> = ({ options, decidingDifference }) => {
  const { t } = useTranslation();
  const labels = useIndicationLabels();
  const withEv = options.filter(o => o.match_evidence && o.match_evidence.indications);
  const visible = useEvidenceVisible(withEv[0]?.match_evidence);
  if (withEv.length < 2 || !visible) return null;
  return (
    <div data-testid="match-compare" className="mt-2 rounded-lg border border-zinc-200 bg-white overflow-x-auto">
      <table className="w-full text-[11.5px]">
        <thead>
          <tr className="bg-zinc-50">
            <th className="text-left px-2 py-1 font-semibold text-zinc-500">{t('chat.matchEvidence.compareTitle', { defaultValue: 'From your photo' })}</th>
            {withEv.map(o => <th key={o.id} className="text-left px-2 py-1 font-semibold text-zinc-800">{o.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {INDICATIONS.map(k => (
            <tr key={k} className="border-t border-zinc-100">
              <td className="px-2 py-1 text-zinc-600">{labels[k]}</td>
              {withEv.map(o => {
                const ind = o.match_evidence!.indications[k];
                const st = ind?.status || 'unknown';
                return (
                  <td key={o.id} className="px-2 py-1" title={ind?.why || ''} data-testid={`compare-${o.id}-${k}`}>
                    <span style={{ color: TONE[st].dot, fontWeight: 700 }}>{TONE[st].mark}</span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {decidingDifference && (
        <div className="px-2 py-1.5 text-[11.5px] text-zinc-700 border-t border-zinc-100">
          <span className="font-semibold">{t('chat.matchEvidence.difference', { defaultValue: 'The difference:' })} </span>
          {decidingDifference}
        </div>
      )}
    </div>
  );
};

export default MatchEvidencePanel;
